/**
 * 段落智能优化对话框
 * 支持基于上下文的段落智能优化，流式输出
 */
import React, { useState, useRef, useEffect } from 'react';
import Toast from './Toast';

interface ParagraphOptimizeDialogProps {
  show: boolean;
  onClose: () => void;
  onReplace: (newText: string) => void;
  selectedText: string;
  chapterTitle?: string;
  chapterDescription?: string;
  selectedModel: string;  // 新增：项目配置的模型
  projectContext?: {
    projectName?: string;
    companyName?: string;
    projectOverview?: string;
  };
}

const ParagraphOptimizeDialog: React.FC<ParagraphOptimizeDialogProps> = ({
  show,
  onClose,
  onReplace,
  selectedText,
  chapterTitle = '',
  chapterDescription = '',
  selectedModel,  // 新增
  projectContext = {},
}) => {
  const [optimizedText, setOptimizedText] = useState('');
  const [instruction, setInstruction] = useState('');
  const [isOptimizing, setIsOptimizing] = useState(false);
  const [toast, setToast] = useState<{ show: boolean; message: string; type: 'success' | 'error' | 'info' }>({
    show: false,
    message: '',
    type: 'info',
  });
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  // 初始化时设置原始文本
  useEffect(() => {
    if (show) {
      setOptimizedText(selectedText);
      setInstruction('');
      setToast({ show: false, message: '', type: 'info' });
      
      // 对话框打开时自动聚焦到输入框
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
        }
      }, 100); // 延迟100ms确保对话框动画完成
    }
  }, [show, selectedText]);

  // 自动调整文本框高度
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = textareaRef.current.scrollHeight + 'px';
    }
  }, [optimizedText]);

  // 智能优化
  const handleOptimize = async () => {
    if (!instruction.trim()) {
      setToast({ show: true, message: '请输入优化要求', type: 'error' });
      return;
    }

    setIsOptimizing(true);
    setToast({ show: false, message: '', type: 'info' });
    
    // 保存当前内容和指令
    const previousText = optimizedText;
    const currentInstruction = instruction.trim();
    
    // 清空输入框，准备下次输入
    setInstruction('');
    
    // 清空文本框，准备接收新内容
    setOptimizedText('');

    try {
      const API_BASE_URL = process.env.REACT_APP_API_URL !== undefined ? process.env.REACT_APP_API_URL : 'http://localhost:8000';
      const response = await fetch(`${API_BASE_URL}/api/content/optimize-paragraph`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(localStorage.getItem('access_token') ? { 'Authorization': `Bearer ${localStorage.getItem('access_token')}` } : {}),
        },
        body: JSON.stringify({
          original_text: previousText || selectedText, // 使用当前内容或原始文字
          instruction: currentInstruction,
          chapter_title: chapterTitle,
          chapter_description: chapterDescription,
          project_name: projectContext.projectName || '',
          company_name: projectContext.companyName || '',
          project_overview: projectContext.projectOverview || '',
          selected_model: selectedModel,  // 使用项目配置的模型
        }),
      });

      if (!response.ok) {
        throw new Error('优化请求失败');
      }

      const reader = response.body?.getReader();
      const decoder = new TextDecoder();

      if (!reader) {
        throw new Error('无法读取响应流');
      }

      let accumulatedText = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value, { stream: true });
        const lines = chunk.split('\n');

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const data = line.slice(6);
            if (data === '[DONE]') {
              continue;
            }
            try {
              const parsed = JSON.parse(data);
              if (parsed.content) {
                accumulatedText += parsed.content;
                setOptimizedText(accumulatedText);
              }
            } catch (e) {
              console.error('解析 SSE 数据失败:', e);
            }
          }
        }
      }

      console.log('✅ 段落优化完成');
      
      // 优化完成后，聚焦到输入框（延迟确保React完成渲染）
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
        }
      }, 0);
    } catch (err) {
      console.error('❌ 段落优化失败:', err);
      setToast({ show: true, message: '优化失败，请重试', type: 'error' });
      // 恢复之前的内容，如果为空则恢复原始文字
      setOptimizedText(previousText || selectedText);
      // 恢复指令
      setInstruction(currentInstruction);
      
      // 失败时也聚焦到输入框
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
        }
      }, 0);
    } finally {
      setIsOptimizing(false);
    }
  };

  // 替换
  const handleReplace = () => {
    if (!optimizedText.trim()) {
      setToast({ show: true, message: '没有可替换的内容', type: 'error' });
      return;
    }
    onReplace(optimizedText);
    onClose();
  };

  // 阻止弹框滚轮事件
  const handleDialogWheel = (e: React.WheelEvent) => {
    e.stopPropagation();
  };

  if (!show) return null;

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[10000] p-4"
      onWheel={handleDialogWheel}
    >
      {/* 强制覆盖外部CSS污染 - 与样式设置弹框完全一致 */}
      <style>{`
        div.paragraph-dialog-header,
        div.paragraph-dialog-header * {
          line-height: normal !important;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif !important;
        }
        div.paragraph-dialog-header h2 {
          font-size: 16px !important;
          line-height: 1.5 !important;
          color: #ffffff !important;
          margin: 0 !important;
          padding: 0 !important;
        }
        #paragraph-dialog-desc,
        p#paragraph-dialog-desc,
        div.paragraph-dialog-header p#paragraph-dialog-desc {
          font-size: 10px !important;
          line-height: 1.2 !important;
          color: rgba(191, 219, 254, 1) !important;
          margin: 0 !important;
          margin-top: 2px !important;
          padding: 0 !important;
        }
      `}</style>
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col">
        {/* 头部 - 与样式设置弹框保持一致 */}
        <div className="paragraph-dialog-header bg-gradient-to-r from-blue-600 via-blue-500 to-cyan-500 px-5 py-3 flex items-center justify-between">
          <div className="flex items-center space-x-2.5">
            <div className="flex-shrink-0 flex items-center justify-center w-8 h-8 rounded-lg bg-white/20 backdrop-blur-sm">
              <svg className="w-4.5 h-4.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
              </svg>
            </div>
            <div>
              <h2 className="text-base font-bold text-white">段落智能优化</h2>
              <p id="paragraph-dialog-desc" className="text-[10px] text-blue-100">AI智能优化段落内容</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-white/90 hover:text-white hover:bg-white/20 rounded-lg p-1.5 transition-all duration-200"
            disabled={isOptimizing}
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* 上下文信息 */}
        {(chapterTitle || projectContext.projectName) && (
          <div className="px-6 py-3 bg-blue-50 border-b border-blue-100">
            <div className="text-sm text-blue-800">
              {projectContext.projectName && (
                <span className="mr-4">📁 项目：{projectContext.projectName}</span>
              )}
              {chapterTitle && (
                <span>📖 章节：{chapterTitle}</span>
              )}
            </div>
          </div>
        )}

        {/* 内容区域 */}
        <div className="flex-1 overflow-auto px-6 py-4 space-y-4">
          {/* 优化后的文本（可编辑） */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              优化后内容 {isOptimizing && <span className="text-blue-600 animate-pulse">（AI 正在优化...）</span>}
            </label>
            <textarea
              ref={textareaRef}
              value={optimizedText}
              onChange={(e) => setOptimizedText(e.target.value)}
              className="w-full min-h-[200px] px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
              style={{
                fontFamily: '"Microsoft YaHei", "微软雅黑", sans-serif',
                fontSize: '12pt',
                lineHeight: '1.75',
                color: 'rgb(17, 24, 39)',
              }}
            />
          </div>

          {/* 优化指令输入框 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              优化要求
            </label>
            <div className="relative">
              <textarea
                ref={inputRef}
                value={instruction}
                onChange={(e) => setInstruction(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey && !isOptimizing) {
                    e.preventDefault();
                    handleOptimize();
                  }
                }}
                placeholder="例如：使语言更专业、增加数据支撑、简化表述、突出优势等..."
                rows={3}
                className="w-full px-4 py-3 pr-12 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
              />
              <button
                onClick={handleOptimize}
                disabled={isOptimizing || !instruction.trim()}
                className="absolute right-2 bottom-[15px] w-8 h-8 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors flex items-center justify-center"
                title="智能优化"
              >
                {isOptimizing ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                ) : (
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
                  </svg>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* 底部按钮 */}
        <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
          <button
            onClick={onClose}
            disabled={isOptimizing}
            className="px-6 py-2 border border-gray-300 text-gray-700 font-bold rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            取消
          </button>
          <button
            onClick={handleReplace}
            disabled={isOptimizing || !optimizedText.trim()}
            className="px-6 py-2 bg-blue-600 text-white font-bold rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
          >
            <span>✓</span>
            <span>替换原文</span>
          </button>
        </div>
      </div>

      {/* Toast 提示 */}
      <Toast
        show={toast.show}
        title=""
        message={toast.message}
        type={toast.type}
        onClose={() => setToast({ ...toast, show: false })}
      />
    </div>
  );
};

export default ParagraphOptimizeDialog;
